import { useState, useEffect, useCallback } from 'react'

interface PromptOutcome {
  outcome: 'accepted' | 'dismissed'
}

interface BeforeInstallPromptEvent extends Event {
  // Modern Chromium (127+) returns the outcome directly from prompt()
  prompt(): Promise<PromptOutcome | void>
  // Deprecated but kept for older browsers
  userChoice: Promise<PromptOutcome>
}

function getIsInstalled() {
  return window.matchMedia('(display-mode: standalone)').matches
}

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(getIsInstalled)

  useEffect(() => {
    if (isInstalled) return

    const handler = (e: Event) => {
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
    }

    const installedHandler = () => {
      setIsInstalled(true)
      setDeferredPrompt(null)
    }

    window.addEventListener('beforeinstallprompt', handler)
    window.addEventListener('appinstalled', installedHandler)

    return () => {
      window.removeEventListener('beforeinstallprompt', handler)
      window.removeEventListener('appinstalled', installedHandler)
    }
  }, [isInstalled])

  const install = useCallback(async () => {
    if (!deferredPrompt) return false
    try {
      const promptResult = await deferredPrompt.prompt()
      // Modern Chromium/Edge returns {outcome} from prompt() directly;
      // older browsers return void and expose the deprecated userChoice property.
      const { outcome } = promptResult ?? await deferredPrompt.userChoice
      setDeferredPrompt(null)
      return outcome === 'accepted'
    } catch (error) {
      console.error('PWA install failed:', error)
      setDeferredPrompt(null)
      return false
    }
  }, [deferredPrompt])

  return {
    canInstall: !!deferredPrompt && !isInstalled,
    isInstalled,
    install,
  }
}
